/**
 * depo.ts
 *
 * NE İŞE YARAR : Oturum belirteci ve cihaz sırrının tarayıcıda saklanması
 * KAYNAK SPEC  : ADR-019 §5.1 (tarayıcıda cihaz = yerel depolama · kabul edilen zayıflık)
 *                ADR-019 §5.2 (iki ayrı süre) · 04-api-sozlesmesi.md AU-01, AU-02
 *                15-arayuz-standardi.md §15.1
 * BAĞLI OLDUĞU : tarayıcının `localStorage`ı
 * KULLANAN     : src/oturum/baglam.tsx — **başka hiçbir yer**
 * DİKKAT       : 1) İKİ SIR AYRI SAKLANIR VE AYRI ÖMÜRLÜDÜR (`04` AU-02). `session.token`
 *                   oturum süresincedir (ADR-008 §5: 30 dk hareketsizlik, azami 12 saat);
 *                   `device_secret` 60 gündür (ADR-019 §5.2). Çıkışta yalnız **belirteç**
 *                   silinir: çıkış yapmak cihaza güvenmeyi bırakmak değildir (`04` AU-06).
 *                   İkisi tek anahtarda tutulsaydı her çıkış 2FA'yı geri getirirdi.
 *                2) `localStorage` SEÇİMİ ADR-019 §5.1'İN KENDİSİDİR: *"cihaz kaydı
 *                   çerez/yerel depolamada tutulur"*. Aynı ADR bunun sonuçlarını da
 *                   kabul ediyor — tarayıcı verisi temizlenirse cihaz tanınmaz, gizli
 *                   sekme her seferinde yeni cihazdır. **Bu bir hata değil, yöntemin
 *                   doğasıdır**; `15` §15.1 giriş ekranında bunun açıklanmasını istiyor.
 *                3) PARMAK İZİ (fingerprint) KULLANILMAZ — ADR-019 §5.1 açıkça yasaklıyor:
 *                   güvenilmez ve KVKK açısından ayrı bir izleme yüzeyi açar. Cihazı
 *                   tanıyan tek şey burada saklanan sırdır.
 *                4) DEPOYA ERİŞİM TEK YERDEN OLUR. `baglam.tsx` dışında hiçbir dosya bu
 *                   modülü çağırmaz; çağırsaydı belirteç iki ayrı yerden yazılabilir ve
 *                   çıkışta biri temizlenmeden kalabilirdi.
 *                5) `localStorage` ERİŞİMİ HATA ATABİLİR (gizli mod kısıtları, site
 *                   verisi kapalı tarayıcı). Atan bir okuma bütün arayüzü boş sayfaya
 *                   çevirirdi; bu yüzden okuma ve yazma sarmalanır ve depo yokmuş gibi
 *                   davranılır — kullanıcı her girişte 2FA görür, ama uygulama çalışır.
 * SON GÜNCELLEME: 2026-08-31 03:15:00
 */

/** Anahtarlar `yurt.` ile öneklenir: aynı köken başka bir uygulamayla paylaşılabilir. */
const BELIRTEC_ANAHTARI = 'yurt.oturum.belirtec';
const SONA_ERER_ANAHTARI = 'yurt.oturum.sonaErer';
const CIHAZ_ANAHTARI = 'yurt.cihaz.sir';

function oku(anahtar: string): string | null {
  try {
    return localStorage.getItem(anahtar);
  } catch {
    // Başlıktaki not 5 — depo yokmuş gibi davranılır.
    return null;
  }
}

function yaz(anahtar: string, deger: string): void {
  try {
    localStorage.setItem(anahtar, deger);
  } catch {
    // Yazılamadıysa oturum yalnız bu sekme boyunca yaşar; uygulama çalışmaya devam eder.
  }
}

function sil(anahtar: string): void {
  try {
    localStorage.removeItem(anahtar);
  } catch {
    // Silinemeyen bir anahtar için yapılabilecek bir şey yok; çökmek daha kötüdür.
  }
}

/** Saklanmış oturum — `04` AU-01/AU-02 yanıtındaki `session` nesnesi. */
export interface SaklananOturum {
  readonly belirtec: string;
  /** ISO 8601, sunucudan geldiği gibi. Yerel saate **burada** çevrilmez. */
  readonly sonaErer: string;
}

export function oturumuOku(): SaklananOturum | null {
  const belirtec = oku(BELIRTEC_ANAHTARI);
  const sonaErer = oku(SONA_ERER_ANAHTARI);
  if (belirtec === null || belirtec === '' || sonaErer === null) {
    return null;
  }
  return { belirtec, sonaErer };
}

export function oturumuYaz(oturum: SaklananOturum): void {
  yaz(BELIRTEC_ANAHTARI, oturum.belirtec);
  yaz(SONA_ERER_ANAHTARI, oturum.sonaErer);
}

/** Yalnız belirteci siler; cihaz sırrına **dokunmaz** (başlıktaki not 1). */
export function oturumuSil(): void {
  sil(BELIRTEC_ANAHTARI);
  sil(SONA_ERER_ANAHTARI);
}

export function cihazSirriniOku(): string | null {
  const sir = oku(CIHAZ_ANAHTARI);
  return sir === '' ? null : sir;
}

export function cihazSirriniYaz(sir: string): void {
  yaz(CIHAZ_ANAHTARI, sir);
}

/**
 * Cihaz sırrını siler.
 *
 * İki yerde çağrılır: kullanıcı kendi cihazını `M2-10` ile kaldırdığında ve sunucu
 * cihazı tanımadığını gösterdiğinde. Silinmeseydi tarayıcı, sunucuda artık karşılığı
 * olmayan bir sırrı her girişte boşuna göndermeye devam ederdi.
 */
export function cihazSirriniSil(): void {
  sil(CIHAZ_ANAHTARI);
}
